/**
 * Shared call views — one implementation for every portal.
 *
 * The One Up admin, Cascade, and Client portals each had their own call list and
 * detail, so polish applied to one never reached the others and Cascade shipped
 * with an empty-looking record. These components are the single version: what a
 * reviewer sees is decided by buildCallPresentation in @command-center/voice-core,
 * and rendered here.
 *
 * Audience differences are a prop, not a fork. `includeAdmin` adds an Internal
 * tab with cost and provider ids; everything else is identical, so a client and
 * an operator are looking at the same reviewed call.
 *
 * Styling uses semantic Tailwind tokens only (bg-card, text-muted-foreground,
 * border) so each portal's own theme supplies the palette — Cascade's maroon and
 * One Up's admin theme both work without per-portal overrides.
 */

import { useState, type ReactNode } from 'react';
import { PhoneCall, type LucideIcon } from 'lucide-react';
import {
  RecordMeta,
  RecordSummaryCard,
  type RegisteredRecordTypeDefinition,
} from '@command-center/ui-system';
import {
  CalloutBlock,
  DetailList,
  EmailLink,
  FieldRows,
  PhoneLink,
  RecordAvatar,
  RecordPill,
  SectionHeading,
  SpecBox,
  formatDateLabel,
  formatDateTimeLabel,
} from './record-presentation';
import {
  buildCallPresentation,
  callTypeBadgeLabel,
  callerRelationshipLabel,
  isInformativeValue,
  isNotableSentiment,
  leadEligibilityLabel,
  leadGradeLabel,
  type CallPresentationInput,
  type CallPresentationSection,
} from '@oneup/shared';

export interface VoiceCallViewProps {
  call: CallPresentationInput & {
    id?: string;
    startedAt?: string | null;
    durationSeconds?: number | null;
    direction?: string | null;
    agentName?: string | null;
    callerRelationship?: string | null;
    callerRelationshipLabel?: string | null;
    leadEligibility?: string | null;
    leadEligibilityLabel?: string | null;
    callCategoryLabel?: string | null;
    isLowValue?: boolean;
    sourceRating?: number;
    archived?: boolean;
  };
  includeAdmin?: boolean;
}

/* ------------------------------------------------------------------ shared bits */

/**
 * Grade is the single most decision-relevant fact on a call, so it reads as a
 * word ("Hot lead") rather than a letter, and carries colour that survives both
 * light and dark themes.
 */
function GradePill({ grade, score }: { grade?: string; score?: number }) {
  const label = isInformativeValue(grade) ? leadGradeLabel(grade) : undefined;
  if (!label) return null;

  const tone = {
    a: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 ring-emerald-500/30',
    b: 'bg-sky-500/15 text-sky-700 dark:text-sky-400 ring-sky-500/30',
    c: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 ring-amber-500/30',
    d: 'bg-orange-500/15 text-orange-700 dark:text-orange-400 ring-orange-500/30',
    unqualified: 'bg-muted text-muted-foreground ring-border',
  }[(grade ?? '').toLowerCase()] ?? 'bg-muted text-muted-foreground ring-border';

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${tone}`}>
      {label}
      {score !== undefined && <span className="opacity-70">{score}/10</span>}
    </span>
  );
}

/**
 * Sentiment, shown only when it departs from the expected. Neutral is the
 * normal outcome of a normal call, so labelling it everywhere trained the eye
 * to skip the row where a negative one actually appears. The stored value is
 * still on the record's Overview.
 */
function SentimentPill({ sentiment }: { sentiment?: string }) {
  if (!isNotableSentiment(sentiment)) return null;
  const tone = {
    positive: 'text-emerald-700 dark:text-emerald-400',
    negative: 'text-red-700 dark:text-red-400',
  }[sentiment!.toLowerCase()] ?? 'text-muted-foreground';
  return <span className={`text-xs capitalize ${tone}`}>{sentiment}</span>;
}

function SourceRatingPill({ rating }: { rating?: number }) {
  if (!rating) return null;
  return <RecordPill tone={rating === 1 ? 'muted' : undefined}>{rating} {rating === 1 ? 'Star' : 'Stars'}</RecordPill>;
}

/**
 * The one lead badge. Grade and eligibility answer the same question — is this
 * worth pursuing? — so showing both puts two competing verdicts side by side.
 * The graded verdict wins where it exists; eligibility stands in only when the
 * analysis produced no grade.
 */
function LeadAssessmentPill({ call, grade, score }: {
  call: VoiceCallViewProps['call'];
  grade?: string;
  score?: number;
}) {
  if (isInformativeValue(grade)) return <GradePill grade={grade} score={score} />;
  if (!isInformativeValue(call.leadEligibility)) return null;
  return (
    <RecordPill tone="muted">
      Lead: {call.leadEligibilityLabel ?? leadEligibilityLabel(call.leadEligibility ?? undefined)}
    </RecordPill>
  );
}

/* ------------------------------------------------------------------- list card */

/**
 * List card: enough to triage without opening the call, and nothing more.
 *
 * Six facts earn a place — who called, the business they represent, one call
 * type, one lead verdict, when and for how long, and the summary. Everything
 * the analysis also produced (caller relationship, the second qualification
 * label, the assistant that answered, sentiment on an ordinary call) lives on
 * the opened record: a card carrying eight badges is a card nobody reads.
 */
export function VoiceCallCard({
  call,
  includeAdmin,
  selected,
  onSelect,
  badges,
  actions,
  primaryAction,
  recordTypeDefinition,
  recordTypeIcon,
}: VoiceCallViewProps & {
  selected?: boolean;
  onSelect?: () => void;
  /**
   * Lifecycle badges from the host portal. Optional: a portal without an
   * intake layer has nothing to say here and gets the grade alone.
   */
  badges?: ReactNode;
  /** Client-safe controls such as opening an authorized recording. */
  actions?: ReactNode;
  /** Optional host-provided primary action pinned to the card footer. */
  primaryAction?: ReactNode;
  recordTypeDefinition?: RegisteredRecordTypeDefinition;
  recordTypeIcon?: LucideIcon;
}) {
  const view = buildCallPresentation(call, { includeAdmin });
  const category = callTypeBadgeLabel(view.grading.category, call.callCategoryLabel);
  const company = view.company && view.company !== view.callerName ? view.company : undefined;

  return (
    <RecordSummaryCard
      recordType="call"
      recordTypeDefinition={recordTypeDefinition}
      recordTypeIcon={recordTypeIcon}
      title={view.callerName}
      onOpen={() => onSelect?.()}
      selected={selected}
      subtitle={company}
      /*
        Two badges: where this call stands, and the one lead verdict. The call
        type, star rating, sentiment, low-value flag, and archived state all
        moved to the meta row below — a card carrying seven pills is a card
        nobody reads, and none of those five is a decision.
      */
      badges={
        <>
          {badges}
          <LeadAssessmentPill call={call} grade={view.grading.leadGrade} score={view.grading.leadScore} />
        </>
      }
      phone={view.phone}
      meta={
        <>
          <RecordMeta>{category}</RecordMeta>
          <RecordMeta>{formatDateLabel(call.startedAt ?? undefined)}</RecordMeta>
          <RecordMeta>
            {call.durationSeconds ? formatDuration(call.durationSeconds) : undefined}
          </RecordMeta>
          <RecordMeta>
            {call.sourceRating ? `${call.sourceRating} ${call.sourceRating === 1 ? 'Star' : 'Stars'}` : undefined}
          </RecordMeta>
          {isNotableSentiment(view.grading.sentiment) && (
            <RecordMeta>
              <span className="capitalize">{view.grading.sentiment}</span>
            </RecordMeta>
          )}
          {call.isLowValue && <RecordMeta>Low Value</RecordMeta>}
          {call.archived && <RecordMeta>Archived</RecordMeta>}
        </>
      }
      summary={view.summary}
      actions={actions}
      primaryAction={primaryAction}
    />
  );
}

/** Condensed row for the split-view list beside an open record. */
export function VoiceCallListItem({
  call,
  includeAdmin,
  selected,
  onSelect,
}: VoiceCallViewProps & { selected?: boolean; onSelect?: () => void }) {
  const view = buildCallPresentation(call, { includeAdmin });
  const category = callTypeBadgeLabel(view.grading.category, call.callCategoryLabel);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors ${
        selected ? 'bg-selection' : 'hover:bg-muted/70'
      }`}
    >
      <RecordAvatar name={view.callerKnown ? view.callerName : undefined} size="sm" icon={!view.callerKnown ? <PhoneCall className="h-3.5 w-3.5" /> : undefined} />
      <span className="min-w-0 flex-1">
        <span className={`block truncate text-[13px] font-medium ${selected ? 'text-primary' : ''}`}>
          {view.callerName}
        </span>
        {(category || call.isLowValue || call.archived || call.sourceRating || isInformativeValue(view.grading.leadGrade)) && (
          <span className="mt-1 flex flex-wrap gap-1">
            {category && <RecordPill>{category}</RecordPill>}
            {call.isLowValue && <RecordPill tone="muted">Low Value</RecordPill>}
            {call.archived && <RecordPill tone="muted">Archived</RecordPill>}
            <GradePill grade={view.grading.leadGrade} score={view.grading.leadScore} />
            <SourceRatingPill rating={call.sourceRating} />
          </span>
        )}
        <span className="mt-1 block truncate text-[11px] text-muted-foreground">
          {[formatDateLabel(call.startedAt ?? undefined), view.phone].filter(Boolean).join('  ')}
        </span>
      </span>
    </button>
  );
}

/* ----------------------------------------------------------------- detail view */

type TabId = 'overview' | 'contact' | 'details' | 'transcript' | 'activity' | 'admin';

/**
 * Detail view. Overview is the call and nothing else — when it happened, who
 * called, what they wanted, the summary, the recording.
 *
 * The Assessment tab is gone. It existed to hold the analysis's verdict, its
 * reasoning, and the controls to overturn it, and it made classification feel
 * like the point of the record. The three pieces went to the three places they
 * belong: the verdict is a badge in the header, the single decision a reviewer
 * actually makes is one inline toggle on Overview, and the reasoning — which
 * analysis ran, what it concluded, and why — moved to Activity, available when
 * someone asks "why did it say that?" and silent when nobody is asking.
 */
export function VoiceCallDetail({
  call,
  includeAdmin,
  transcript,
  recording,
  actions,
  overviewBanner,
  actionCenter,
  eligibilityToggle,
  contact,
  activity,
  recordManagement,
}: VoiceCallViewProps & {
  transcript?: string | null;
  recording?: ReactNode;
  actions?: ReactNode;
  overviewBanner?: ReactNode;
  /**
   * Where a reviewer says what happened and what should happen next. Sits
   * directly under the banner that states where this call stands, so reading
   * the situation and answering it are one movement down the page.
   */
  actionCenter?: ReactNode;
  /** The one inline decision on Overview: is this a Lead worth pursuing? */
  eligibilityToggle?: ReactNode;
  /** Confirmed business Contact relationship, including an explicit empty state. */
  contact?: ReactNode;
  /** Analysis history and notes — what ran, what it decided, and why. */
  activity?: ReactNode;
  recordManagement?: ReactNode;
}) {
  const view = buildCallPresentation(call, { includeAdmin });
  const [tab, setTab] = useState<TabId>('overview');

  const tabs: Array<{ id: TabId; label: string; show: boolean }> = [
    { id: 'overview', label: 'Overview', show: true },
    { id: 'contact', label: 'Contact', show: Boolean(contact) },
    { id: 'details', label: 'Details', show: view.analysisSections.length > 0 },
    { id: 'transcript', label: 'Transcript', show: Boolean(transcript) },
    { id: 'activity', label: 'Activity', show: Boolean(activity) },
    { id: 'admin', label: 'Internal', show: Boolean(includeAdmin && view.adminSections.length > 0) },
  ];
  const visible = tabs.filter((entry) => entry.show);

  return (
    <div className="flex h-full flex-col">
      <header className="border-b p-4">
        <div className="flex items-start gap-3">
          <RecordAvatar
            name={view.callerKnown ? view.callerName : undefined}
            icon={!view.callerKnown ? <PhoneCall className="h-4 w-4" /> : undefined}
          />
          <div className="min-w-0 flex-1">
            <h2 className={`truncate text-lg font-semibold ${view.callerKnown ? '' : 'text-muted-foreground'}`}>
              {view.callerName}
            </h2>
            {view.company && view.company !== view.callerName && (
              <p className="truncate text-sm text-muted-foreground">{view.company}</p>
            )}
            {/*
              The header carries the same short list as the card. Caller
              relationship and the assistant that answered stay in the Overview
              spec box rather than competing here.
            */}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {callTypeBadgeLabel(view.grading.category, call.callCategoryLabel) && (
                <RecordPill>{callTypeBadgeLabel(view.grading.category, call.callCategoryLabel)}</RecordPill>
              )}
              {call.isLowValue && <RecordPill tone="muted">Low Value</RecordPill>}
              {call.archived && <RecordPill tone="muted">Archived</RecordPill>}
              <span className="text-xs text-muted-foreground">
                {formatDateLabel(call.startedAt ?? undefined)}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <LeadAssessmentPill call={call} grade={view.grading.leadGrade} score={view.grading.leadScore} />
            <SourceRatingPill rating={call.sourceRating} />
            {actions}
          </div>
        </div>
      </header>

      {visible.length > 1 && (
        <nav className="flex gap-1 border-b px-2" role="tablist">
          {visible.map((entry) => (
            <button
              key={entry.id}
              role="tab"
              aria-selected={tab === entry.id}
              type="button"
              onClick={() => setTab(entry.id)}
              className={`px-3 py-2 text-sm transition-colors ${
                tab === entry.id
                  ? 'border-b-2 border-primary font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {entry.label}
            </button>
          ))}
        </nav>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'overview' && (
          <div className="space-y-6">
            {/*
              When and how long leads, because that is what a reviewer checks
              first. Lead eligibility left this box to become the inline toggle
              below — it is a decision, not a fact about the call.
            */}
            <SpecBox items={[
              { label: 'Started', value: formatDateTimeLabel(call.startedAt ?? undefined) },
              { label: 'Duration', value: call.durationSeconds ? formatDuration(call.durationSeconds) : undefined },
              { label: 'Direction', value: formatDirection(call.direction) },
              {
                label: 'Sentiment',
                value: view.grading.sentiment
                  ? <span className="capitalize">{view.grading.sentiment}</span>
                  : undefined,
              },
              { label: 'Answered by', value: call.agentName ?? undefined },
              {
                label: 'Caller relationship',
                value: call.callerRelationshipLabel
                  ?? callerRelationshipLabel(call.callerRelationship ?? undefined),
              },
            ]} />

            {eligibilityToggle}

            {overviewBanner}

            {actionCenter}

            <section>
              <SectionHeading>Caller</SectionHeading>
              <DetailList rows={[
                { label: 'Name', value: view.callerKnown ? view.callerName : undefined },
                { label: 'Phone', value: <PhoneLink value={view.phone} /> },
                { label: 'Email', value: <EmailLink value={view.email} /> },
                { label: 'Company', value: view.company },
              ]} />
            </section>

            {view.highlights.length > 0 && (
              <section>
                <SectionHeading>What they wanted</SectionHeading>
                <FieldRows rows={view.highlights.map((field) => ({
                  label: field.label,
                  value: field.value,
                }))} />
              </section>
            )}

            {view.summary ? (
              <section>
                <SectionHeading>Summary</SectionHeading>
                <CalloutBlock>{view.summary}</CalloutBlock>
              </section>
            ) : (
              <p className="text-sm text-muted-foreground">No summary was produced for this call.</p>
            )}

            {/* "Why this grade" moved to Activity with the rest of the
                analysis reasoning. The grade badge itself stays in the header. */}

            {recording && <section>{recording}</section>}

            {/* Notes and archiving close the page: they act on the record, not on the call. */}
            {recordManagement}
          </div>
        )}

        {tab === 'activity' && <div className="space-y-5">{activity}</div>}

        {tab === 'contact' && <div className="space-y-5">{contact}</div>}

        {tab === 'details' && (
          <div className="space-y-5">
            {view.analysisSections.map((section) => (
              <Section key={section.id} section={section} />
            ))}
          </div>
        )}

        {tab === 'transcript' && (
          <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed text-muted-foreground">
            {transcript}
          </pre>
        )}

        {tab === 'admin' && (
          <div className="space-y-5">
            {view.adminSections.map((section) => (
              <Section key={section.id} section={section} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ section }: { section: CallPresentationSection }) {
  return (
    <section>
      <SectionHeading>{section.title}</SectionHeading>
      {section.text && <CalloutBlock>{section.text}</CalloutBlock>}
      {section.fields.length > 0 && (
        <FieldRows rows={section.fields.map((field) => ({
          label: field.label,
          // Contact values stay actionable wherever they surface.
          value: /email/i.test(field.label) && field.value.includes('@')
            ? <EmailLink value={field.value} />
            : /phone|mobile|cell/i.test(field.label)
              ? <PhoneLink value={field.value} />
              : field.value,
        }))} />
      )}
    </section>
  );
}

/** "inboundPhoneCall" is a provider token, not a phrase a client should read. */
function formatDirection(direction?: string | null): string | undefined {
  if (!direction) return undefined;
  return {
    inboundPhoneCall: 'Inbound call',
    outboundPhoneCall: 'Outbound call',
    webCall: 'Web call',
  }[direction] ?? direction.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}
